import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  Input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Language, Post } from 'src/app/core/models/core.models';
import {
  injectCreateSynopsisMutation,
  injectDeleteSynopsisMutation,
  injectGenerateAISynopsisMutation,
  injectUpdateSynopsisMutation,
} from 'src/app/api/queries/post-api.queries';

import { FormGroup } from '@angular/forms';
import { FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { NomLanguages } from 'src/app/core/constants';
import { Observable } from 'rxjs';
import { PostDetailsService } from 'src/app/core/services/post-details.service';
import { TranslateService } from '@ngx-translate/core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-post-synopsis',
  templateUrl: './post-synopsis.component.html',
  styleUrls: ['./post-synopsis.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostSynopsisComponent implements OnChanges {
  @Input() postId: string;

  private readonly postDetails = inject(PostDetailsService);

  /** The post this tab is showing - the pane pushes it through `ngOnChanges`. */
  private readonly shownPostId = signal<string | null>(null);

  // `toObservable` needs an injection context, so this is a field; `ngOnInit` only
  // subscribes.
  post$: Observable<Post> = toObservable(
    computed(() => this.postDetails.postFor(this.shownPostId())),
  );
  post: Post;
  faSpinner = faSpinner;
  currentLanguage: Language = {
    name: 'English',
    id: NomLanguages.ENGLISH,
    code: 'en',
    rtl: false,
  };
  hasSynopsisLanguage: boolean = false;
  hasTranslationLangauge: boolean = false;

  private destroyRef = inject(DestroyRef);

  private readonly createSynopsisMutation = injectCreateSynopsisMutation();
  private readonly updateSynopsisMutation = injectUpdateSynopsisMutation();
  private readonly generateAISynopsisMutation =
    injectGenerateAISynopsisMutation();
  private readonly deleteSynopsisMutation = injectDeleteSynopsisMutation();

  // Guarded on the post the mutation variables carry: this instance is reused
  // across posts, so a bare `isPending` would spin on the wrong one.
  postSynopsisLoading$: Observable<boolean> = toObservable(
    computed(
      () =>
        this.pendingForShownPost(
          this.updateSynopsisMutation.isPending(),
          this.updateSynopsisMutation.variables(),
        ) ||
        this.pendingForShownPost(
          this.generateAISynopsisMutation.isPending(),
          this.generateAISynopsisMutation.variables(),
        ),
    ),
  );

  formGroup: FormGroup;
  formProperties: FormProperty[] = [
    {
      propertyName: 'language_picker',
      controlType: 'language-picker',
      additionalConfig: {
        hasDefault: false,
      },
    },
    {
      propertyName: 'title',
      propertyNameLanguages: {
        [NomLanguages.BULGARIAN]:
          'synopsis.' + NomLanguages.BULGARIAN + '.title',
        [NomLanguages.ENGLISH]: 'synopsis.' + NomLanguages.ENGLISH + '.title',
        [NomLanguages.ARABIC]: 'synopsis.' + NomLanguages.ARABIC + '.title',
      },
      propertyLabel: this.translateService.instant(
        'post-sypnosis.label.sypnosis-title',
      ),
      controlType: 'text',
      additionalConfig: {
        placeholder: this.translateService.instant(
          'post-sypnosis.placeholder.sypnosis-title',
        ),
      },
    },
    {
      propertyName: 'synopsis',
      propertyLabel: this.translateService.instant(
        'post-sypnosis.label.sypnosis',
      ),
      controlType: 'textarea',
      propertyNameLanguages: {
        [NomLanguages.BULGARIAN]:
          'synopsis.' + NomLanguages.BULGARIAN + '.synopsis',
        [NomLanguages.ENGLISH]:
          'synopsis.' + NomLanguages.ENGLISH + '.synopsis',
        [NomLanguages.ARABIC]: 'synopsis.' + NomLanguages.ARABIC + '.synopsis',
      },
      additionalConfig: {
        placeholder: this.translateService.instant(
          'post-sypnosis.placeholder.sypnosis',
        ),
        rows: 20,
      },
    },
  ];

  constructor(
    private formService: FormsService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {
    this.formGroup = this.formService.createFormGroup(this.formProperties);
  }

  ngOnInit() {
    this.shownPostId.set(this.postId);
    this.post$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((post) => {
      this.post = post;
      this.formService.populateFormValues(this.formGroup, post);
      this.estimateHasSynopsisLanguage();
    });
  }

  /**
   * The pane keeps one instance of this tab and re-pushes `postId` when the selected
   * post changes, so the panel has to follow it rather than bind once.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postId']?.currentValue) {
      this.shownPostId.set(this.postId);
    }
  }

  private pendingForShownPost(
    isPending: boolean,
    variables: { postId: string } | undefined,
  ): boolean {
    return isPending && variables?.postId === this.shownPostId();
  }

  estimateHasSynopsisLanguage() {
    if (!this.post) return;

    this.hasSynopsisLanguage =
      !!this.post.synopsis &&
      this.currentLanguage.id in this.post.synopsis &&
      this.isValidObject(this.post.synopsis[this.currentLanguage.id]);
    this.cdr.detectChanges();
  }

  isValidObject(obj: any): boolean {
    return obj && Object.keys(obj).length > 0;
  }

  get currentSynopsisId() {
    return this.post.synopsis[this.currentLanguage.id]?.id;
  }

  createSynopsis() {
    const currentLanguageId = this.currentLanguage.id;
    const title = this.formGroup.get([
      'synopsis.' + currentLanguageId + '.title',
    ]).value;
    const synopsis = this.formGroup.get([
      'synopsis.' + currentLanguageId + '.synopsis',
    ]).value;
    this.createSynopsisMutation.mutate({
      postId: this.post.id,
      languageId: this.currentLanguage.id,
      title: title,
      synopsis: synopsis,
      feedId: '',
    });
  }
  updateSynopsis() {
    const currentLanguageId = this.currentLanguage.id;
    const title = this.formGroup.get([
      'synopsis.' + currentLanguageId + '.title',
    ]).value;
    const synopsis = this.formGroup.get([
      'synopsis.' + currentLanguageId + '.synopsis',
    ]).value;
    this.updateSynopsisMutation.mutate({
      postId: this.post.id,
      id: this.currentSynopsisId,
      title: title,
      synopsis: synopsis,
    });
  }

  aiGenerateSynopsis() {
    this.generateAISynopsisMutation.mutate({
      postId: this.post.id,
      languageId: this.currentLanguage.id,
    });
  }

  copyFromOriginal() {
    this.formGroup.patchValue({
      ['synopsis.' + this.currentLanguage.id + '.title']: this.post.title,
      ['synopsis.' + this.currentLanguage.id + '.synopsis']: this.post.content,
    });
  }

  copyFromTranslation() {
    if (this.post.type !== 'social') {
      this.formGroup.patchValue({
        ['synopsis.' + this.currentLanguage.id + '.title']:
          this.post.translation?.[this.currentLanguage.id]?.title,
      });
    }
    this.formGroup.patchValue({
      ['synopsis.' + this.currentLanguage.id + '.synopsis']:
        this.post.translation?.[this.currentLanguage.id]?.content,
    });
  }

  deleteSynopsis() {
    this.deleteSynopsisMutation.mutate({
      postId: this.post.id,
      id: this.currentSynopsisId,
    });
  }

  onLanguageChanged(language: Language) {
    this.currentLanguage = language;
    this.estimateHasSynopsisLanguage();
  }
}
